import { Component, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { finalize, map, Subscription, takeWhile, timer } from 'rxjs';
import { AuthenticationService } from '../_services/authentication.service';
import { isAgilPlatform, isDinHmsPlatform } from '../shared/common/common-functions.util';
import { Application } from '../_models/feature';

@Component({
  selector: 'forget-password',
  templateUrl: 'forget-password.component.html',
  styleUrls: ['../login/login-aura.css', 'forget-password.component.css', '../login/mdco-auth.scss'],
  standalone: false
})
export class ForgetPasswordComponent implements OnInit, OnDestroy {
  ForgotPasswordForm: FormGroup;
  isNotValid = false;
  mailSent = false;
  loading = false;
  resendSeconds = 0;
  private resendCooldown?: Subscription;
  private subscriptions: Subscription[] = [];

  constructor(
    private authenticationService: AuthenticationService,
    private formBuilder: FormBuilder,
    private translate: TranslateService,
    private renderer: Renderer2
  ) {}

  get isMedi3(): boolean {
    return window.location.pathname.includes('medi3');
  }

  /** Agil Helse's own recovery pages, served under /agil. */
  get isAgil(): boolean {
    return isAgilPlatform();
  }

  get isDinHms(): boolean {
    return isDinHmsPlatform();
  }

  /** The hero's standing line names the product, so each brand has its own. */
  get storyIntroKey(): string {
    if (this.isMedi3) return 'PASSWORD_RECOVERY.MEDI3_STORY_INTRO';
    if (this.isAgil) return 'PASSWORD_RECOVERY.AGIL_STORY_INTRO';
    return 'PASSWORD_RECOVERY.STORY_INTRO';
  }

  /**
   * Application the reset mail is sent on behalf of. It decides both the
   * branding of the mail and the host its link points back at, so an Agil user
   * who asks for a reset here must not be handed a Kuba mail.
   *
   * Medi3 is the backend's ApplicationEnum.MEDI3 = 5, which the frontend's own
   * Application enum does not carry; spelled out here rather than added to that
   * enum, which is shared and not this change's to widen.
   */
  private get applicationId(): number {
    if (this.isMedi3) return 5;
    if (this.isAgil) return Application.AGIL;
    return Application.IK;
  }

  get currentLang(): 'en' | 'no' {
    return this.translate.currentLang() === 'en' ? 'en' : 'no';
  }

  ngOnInit(): void {
    this.ForgotPasswordForm = this.formBuilder.group({
      checkUser: this.formBuilder.group({
        username: ['', [Validators.required, Validators.pattern(/\S/), Validators.maxLength(254), Validators.pattern(/^[^\u0000-\u001f\u007f-\u009f]+$/)]]
      })
    });
    this.renderer.addClass(document.documentElement, 'loginView');
    this.renderer.addClass(document.body, 'loginView');
  }

  setLanguage(lang: 'en' | 'no'): void {
    this.translate.use(lang);
    localStorage.setItem('languageId', lang === 'en' ? '1' : '2');
    localStorage.setItem('languageMode', lang === 'en' ? 'en-GB' : 'no-NO');
    document.documentElement.lang = lang;
  }

  sendMail(): void {
    if (this.loading || this.mailSent || this.resendSeconds > 0) return;
    this.ForgotPasswordForm.markAllAsTouched();
    if (this.ForgotPasswordForm.invalid) return;
    this.loading = true;
    this.isNotValid = false;
    const username = this.ForgotPasswordForm.value.checkUser.username.trim();
    this.subscriptions.push(
      this.authenticationService
        .forgotPassword(
          username,
          this.currentLang === 'en' ? 1 : 2,
          this.applicationId
        )
        .pipe(
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: () => {
            this.mailSent = true;
            this.startResendCooldown();
          },
          error: error => {
            this.isNotValid = true;
            if (error.status === 429) {
              const retryAfter = Number(error.headers?.get('Retry-After'));
              this.startResendCooldown(Number.isFinite(retryAfter) && retryAfter > 0 ? Math.ceil(retryAfter) : 180);
            }
          }
        })
    );
  }

  resendMail(): void {
    if (!this.mailSent || this.loading || this.resendSeconds > 0) return;
    this.mailSent = false;
    this.sendMail();
  }

  private startResendCooldown(seconds = 60): void {
    this.resendCooldown?.unsubscribe();
    this.resendSeconds = seconds;
    const availableAt = Date.now() + seconds * 1000;
    // Use elapsed time so background-tab timer throttling does not extend the wait.
    this.resendCooldown = timer(1000, 1000)
      .pipe(
        map(() => Math.max(0, Math.ceil((availableAt - Date.now()) / 1000))),
        takeWhile(seconds => seconds > 0, true)
      )
      .subscribe(seconds => {
        this.resendSeconds = seconds;
      });
  }

  clear(): void {
    if (this.loading) return;
    this.ForgotPasswordForm.reset();
    this.isNotValid = false;
    this.mailSent = false;
  }

  ngOnDestroy(): void {
    this.resendCooldown?.unsubscribe();
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.renderer.removeClass(document.documentElement, 'loginView');
    this.renderer.removeClass(document.body, 'loginView');
  }
}
